import React from "react";
import { Icon } from "@iconify/react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  WorkflowFieldInputType,
  WorkflowRequirement,
  WorkflowRequirementFilter,
  WorkflowRequirementKind,
} from "../../../types/Transaction";
import {
  CheckBoxWithLabel,
  Input,
  Label,
  TextArea,
} from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import {
  HStack,
  IconButtonType,
  IconCTAButton,
  IconSize,
  SecondaryCTAButton,
  VStack,
} from "../../../components/utils";

const FILE_TYPES = [
  ["PDF", "application/pdf"],
  ["JPEG", "image/jpeg"],
  ["PNG", "image/png"],
] as const;

const REQUIREMENT_KINDS = Object.values(WorkflowRequirementKind);
const INPUT_TYPES = Object.values(WorkflowFieldInputType);
type RequirementFilter = WorkflowRequirementFilter | WorkflowRequirementKind;

const kindLabel = (kind: WorkflowRequirement["kind"]) =>
  kind === WorkflowRequirementKind.FIELD
    ? "Field"
    : kind === WorkflowRequirementKind.DOCUMENT
    ? "Document"
    : kind === WorkflowRequirementKind.DECLARATION
    ? "Declaration"
    : "Question";

const createRequirementId = () =>
  Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

type Props = {
  requirements: WorkflowRequirement[];
  onChange: (requirements: WorkflowRequirement[]) => void;
};

function SortableRequirementRow({
  item,
  index,
  onRemove,
  expanded,
  onToggle,
  showDivider,
  idPrefix,
  children,
}: {
  item: WorkflowRequirement;
  index: number;
  onRemove: () => void;
  expanded: boolean;
  onToggle: () => void;
  showDivider: boolean;
  idPrefix: string;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
      }}
    >
      <VStack
        className={`${
          showDivider ? "border-b border-borderColor" : ""
        } bg-white ${isDragging ? "shadow-card opacity-90" : ""}`}
      >
        <HStack className="w-full items-center gap-2 py-2">
          <button
            type="button"
            className="shrink-0 cursor-grab touch-none text-gray-300 hover:text-gray-500 active:cursor-grabbing"
            title="Drag to reorder"
            aria-label={`Drag ${kindLabel(item.kind).toLowerCase()} ${
              index + 1
            } to reorder`}
            {...attributes}
            {...listeners}
          >
            <Icon icon="mdi:drag-vertical" width={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center justify-between gap-2 py-2 text-left focus-visible:ring-2 focus-visible:ring-primary"
            aria-expanded={expanded}
            aria-controls={
              expanded ? `${idPrefix}-editor-${item.id}` : undefined
            }
            onClick={onToggle}
          >
            <span className="min-w-0">
              <span className="block text-xs text-secondaryText">
                {kindLabel(item.kind)} {index + 1} ·{" "}
                {item.required ? "Mandatory" : "Optional"}
              </span>
              <span className="block truncate text-sm font-semibold text-headerColor">
                {item.kind === WorkflowRequirementKind.DECLARATION
                  ? item.declaration?.text || "Add declaration content"
                  : item.label ||
                    `Untitled ${kindLabel(item.kind).toLowerCase()}`}
              </span>
            </span>
            <Icon
              icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
              width={18}
              className="shrink-0 text-secondaryText"
              aria-hidden="true"
            />
          </button>
          <IconCTAButton
            aria-label={`Remove ${kindLabel(item.kind).toLowerCase()} ${
              index + 1
            }`}
            title="Remove item"
            iconName="material-symbols:delete-outline-rounded"
            iconOnly
            iconSize={IconSize.M}
            iconClassName="text-red-600"
            buttonType={IconButtonType.NONE}
            onClick={onRemove}
          />
        </HStack>
        {expanded && (
          <div id={`${idPrefix}-editor-${item.id}`} className="pb-4 pl-6">
            {children}
          </div>
        )}
      </VStack>
    </div>
  );
}

export default function WorkflowRequirementEditor({
  requirements,
  onChange,
}: Props) {
  const [newKind, setNewKind] = React.useState<WorkflowRequirement["kind"]>(
    WorkflowRequirementKind.FIELD
  );
  const [filter, setFilter] = React.useState<RequirementFilter>(
    WorkflowRequirementFilter.ALL
  );
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const idPrefix = React.useId();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ordered = [...requirements].sort((a, b) => a.order - b.order);
  const visibleItems = ordered
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        filter === WorkflowRequirementFilter.ALL || item.kind === filter
    );
  const filterOptions: {
    value: RequirementFilter;
    label: string;
    count: number;
  }[] = [
    {
      value: WorkflowRequirementFilter.ALL,
      label: "All requirements",
      count: ordered.length,
    },
    {
      value: WorkflowRequirementKind.FIELD,
      label: "Data inputs",
      count: ordered.filter(
        (item) => item.kind === WorkflowRequirementKind.FIELD
      ).length,
    },
    {
      value: WorkflowRequirementKind.DOCUMENT,
      label: "Document proofs",
      count: ordered.filter(
        (item) => item.kind === WorkflowRequirementKind.DOCUMENT
      ).length,
    },
    {
      value: WorkflowRequirementKind.DECLARATION,
      label: "Declarations",
      count: ordered.filter(
        (item) => item.kind === WorkflowRequirementKind.DECLARATION
      ).length,
    },
    {
      value: WorkflowRequirementKind.QUESTION,
      label: "Questions",
      count: ordered.filter(
        (item) => item.kind === WorkflowRequirementKind.QUESTION
      ).length,
    },
  ];
  const update = (id: string, patch: Partial<WorkflowRequirement>) =>
    onChange(
      ordered.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  const add = () => {
    const id = createRequirementId();
    onChange([
      ...ordered,
      {
        id,
        kind: newKind,
        order: ordered.length + 1,
        ...(newKind === WorkflowRequirementKind.DECLARATION
          ? {}
          : { label: "", description: "" }),
        required: true,
        ...(newKind === WorkflowRequirementKind.FIELD
          ? { field: { inputType: WorkflowFieldInputType.TEXT } }
          : newKind === WorkflowRequirementKind.DOCUMENT
          ? {
              document: {
                acceptedTypes: FILE_TYPES.map(([, mime]) => mime),
                maxSizeMB: 10,
                maxFiles: 1,
              },
            }
          : newKind === WorkflowRequirementKind.DECLARATION
          ? { declaration: { text: "" } }
          : {
              question: {
                options: [
                  { id: createRequirementId(), label: "Yes" },
                  { id: createRequirementId(), label: "No" },
                ],
              },
            }),
      },
    ]);
    setExpandedId(id);
  };
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const source = visibleItems.findIndex(({ item }) => item.id === active.id);
    const target = visibleItems.findIndex(({ item }) => item.id === over.id);
    if (source < 0 || target < 0) return;
    const reorderedVisible = arrayMove(
      visibleItems.map(({ item }) => item),
      source,
      target
    );
    const next = [...ordered];
    visibleItems.forEach(({ index }, position) => {
      next[index] = reorderedVisible[position];
    });
    onChange(next.map((item, order) => ({ ...item, order: order + 1 })));
  };
  const remove = (index: number) => {
    if (expandedId === ordered[index].id) setExpandedId(null);
    onChange(
      ordered
        .filter((_, rowIndex) => rowIndex !== index)
        .map((item, order) => ({ ...item, order: order + 1 }))
    );
  };

  return (
    <section className="border-t border-borderColor bg-white px-4 py-5">
      <div className="max-w-4xl">
        <VStack className="gap-1">
          <h3 className="text-base font-semibold text-headerColor">
            Information required from employee
          </h3>
          <p className="text-sm text-secondaryText">
            Configure the details employees provide before completing this step.
          </p>
        </VStack>
        <HStack
          className="mt-5 flex-wrap items-center gap-2"
          role="group"
          aria-label="Requirement categories"
        >
          {filterOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={filter === option.value}
              onClick={() => {
                setFilter(option.value);
                if (option.value !== WorkflowRequirementFilter.ALL)
                  setNewKind(option.value);
              }}
              className={`rounded-sm border px-3 py-2 text-xs2 font-medium focus-visible:ring-2 focus-visible:ring-primary ${
                filter === option.value
                  ? "border-secondary bg-secondary text-white"
                  : "border-borderColor bg-white text-secondaryText hover:bg-tertiaryBg"
              }`}
            >
              {option.label} ({option.count})
            </button>
          ))}
        </HStack>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleItems.map(({ item }) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <VStack className="mt-4">
              {visibleItems.length === 0 && (
                <p className="rounded-lg border border-dashed border-borderColor p-4 text-sm text-secondaryText">
                  No{" "}
                  {filter === WorkflowRequirementFilter.ALL
                    ? "requirements"
                    : `${kindLabel(filter).toLowerCase()} items`}{" "}
                  configured yet.
                </p>
              )}
              {visibleItems.map(({ item, index }) => (
                <SortableRequirementRow
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={() => remove(index)}
                  showDivider={
                    filter !== WorkflowRequirementFilter.ALL ||
                    ordered[index + 1]?.kind !==
                      WorkflowRequirementKind.DECLARATION
                  }
                  expanded={expandedId === item.id}
                  idPrefix={idPrefix}
                  onToggle={() =>
                    setExpandedId((current) =>
                      current === item.id ? null : item.id
                    )
                  }
                >
                  <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
                    {item.kind !== WorkflowRequirementKind.DECLARATION && (
                      <VStack className="gap-2">
                        <Label
                          htmlFor={`${idPrefix}-requirement-label-${item.id}`}
                        >
                          Label
                        </Label>
                        <Input
                          id={`${idPrefix}-requirement-label-${item.id}`}
                          type="text"
                          value={item.label ?? ""}
                          maxLength={120}
                          onChange={(event) =>
                            update(item.id, { label: event.target.value })
                          }
                          placeholder={
                            item.kind === WorkflowRequirementKind.FIELD
                              ? "e.g. Employee ID"
                              : item.kind === WorkflowRequirementKind.DOCUMENT
                              ? "e.g. Bank statement"
                              : "e.g. Do you have an Aadhaar card?"
                          }
                        />
                      </VStack>
                    )}
                    <HStack
                      className={
                        item.kind === WorkflowRequirementKind.DECLARATION
                          ? "items-center sm:col-span-2"
                          : "items-end pb-4"
                      }
                    >
                      <CheckBoxWithLabel
                        id={`${idPrefix}-requirement-required-${item.id}`}
                        label="Mandatory For Employee"
                        labelclassname="ml-2 text-sm text-primaryText"
                        className="h-4 w-4 accent-primary"
                        checked={item.required}
                        onChange={(event) =>
                          update(item.id, { required: event.target.checked })
                        }
                      />
                    </HStack>
                    {item.kind !== WorkflowRequirementKind.DECLARATION && (
                      <VStack className="gap-2 sm:col-span-2">
                        <Label
                          htmlFor={`${idPrefix}-requirement-description-${item.id}`}
                        >
                          Description for employee
                        </Label>
                        <TextArea
                          id={`${idPrefix}-requirement-description-${item.id}`}
                          value={item.description ?? ""}
                          maxLength={500}
                          onChange={(event) =>
                            update(item.id, { description: event.target.value })
                          }
                          rows={2}
                        />
                      </VStack>
                    )}
                    {item.kind === WorkflowRequirementKind.QUESTION ? (
                      <VStack className="max-w-xl gap-2 sm:col-span-2">
                        <Label>Answer options (2 to 5)</Label>
                        {(item.question?.options ?? []).map(
                          (option, optionIndex) => (
                            <HStack
                              key={option.id}
                              className="items-center gap-2"
                            >
                              <Label
                                htmlFor={`${idPrefix}-question-option-${item.id}-${option.id}`}
                                className="w-16 shrink-0 text-sm"
                              >
                                {optionIndex + 1}.
                              </Label>
                              <div className="min-w-0 flex-1">
                                <Input
                                  id={`${idPrefix}-question-option-${item.id}-${option.id}`}
                                  className="w-full"
                                  value={option.label}
                                  maxLength={120}
                                  onChange={(event) =>
                                    update(item.id, {
                                      question: {
                                        options: item.question!.options.map(
                                          (entry) =>
                                            entry.id === option.id
                                              ? {
                                                  ...entry,
                                                  label: event.target.value,
                                                }
                                              : entry
                                        ),
                                      },
                                    })
                                  }
                                />
                              </div>
                              <IconCTAButton
                                aria-label={`Remove option ${optionIndex + 1}`}
                                title="Remove option"
                                iconName="material-symbols:delete-outline-rounded"
                                iconOnly
                                iconSize={IconSize.M}
                                iconClassName="text-red-600"
                                buttonType={IconButtonType.NONE}
                                disabled={
                                  (item.question?.options.length ?? 0) <= 2
                                }
                                onClick={() =>
                                  update(item.id, {
                                    question: {
                                      options: item.question!.options.filter(
                                        (entry) => entry.id !== option.id
                                      ),
                                    },
                                  })
                                }
                              />
                            </HStack>
                          )
                        )}
                        <SecondaryCTAButton
                          className="self-start"
                          disabled={(item.question?.options.length ?? 0) >= 5}
                          onClick={() =>
                            update(item.id, {
                              question: {
                                options: [
                                  ...(item.question?.options ?? []),
                                  { id: createRequirementId(), label: "" },
                                ],
                              },
                            })
                          }
                        >
                          Add option
                        </SecondaryCTAButton>
                      </VStack>
                    ) : item.kind === WorkflowRequirementKind.DECLARATION ? (
                      <VStack className="gap-2 sm:col-span-2">
                        <Label htmlFor={`${idPrefix}-declaration-${item.id}`}>
                          Declaration content
                        </Label>
                        <TextArea
                          id={`${idPrefix}-declaration-${item.id}`}
                          className="w-full"
                          value={item.declaration?.text ?? ""}
                          maxLength={2000}
                          rows={4}
                          onChange={(event) =>
                            update(item.id, {
                              declaration: { text: event.target.value },
                            })
                          }
                        />
                      </VStack>
                    ) : item.kind === WorkflowRequirementKind.FIELD ? (
                      <>
                        <VStack className="gap-2">
                          <Label htmlFor={`${idPrefix}-input-type-${item.id}`}>
                            Input type
                          </Label>
                          <Select
                            id={`${idPrefix}-input-type-${item.id}`}
                            options={[...INPUT_TYPES]}
                            value={
                              item.field?.inputType ??
                              WorkflowFieldInputType.TEXT
                            }
                            textGetter={(option) =>
                              option.charAt(0) + option.slice(1).toLowerCase()
                            }
                            onChange={(event) =>
                              update(item.id, {
                                field: {
                                  inputType: event.target
                                    .value as WorkflowFieldInputType,
                                },
                              })
                            }
                          />
                        </VStack>
                        {item.field?.inputType ===
                          WorkflowFieldInputType.TEXT && (
                          <VStack className="gap-2">
                            <Label
                              htmlFor={`${idPrefix}-max-length-${item.id}`}
                            >
                              Maximum characters
                            </Label>
                            <Input
                              id={`${idPrefix}-max-length-${item.id}`}
                              type="number"
                              min={1}
                              max={1000}
                              value={item.field.maxLength ?? ""}
                              onChange={(event) =>
                                update(item.id, {
                                  field: {
                                    ...item.field!,
                                    maxLength: event.target.value
                                      ? Number(event.target.value)
                                      : undefined,
                                  },
                                })
                              }
                            />
                          </VStack>
                        )}
                      </>
                    ) : (
                      <>
                        <VStack className="gap-2">
                          <Label htmlFor={`${idPrefix}-max-size-${item.id}`}>
                            Maximum size per file (MB)
                          </Label>
                          <Input
                            id={`${idPrefix}-max-size-${item.id}`}
                            type="number"
                            min={1}
                            max={25}
                            value={item.document?.maxSizeMB ?? 10}
                            onChange={(event) =>
                              update(item.id, {
                                document: {
                                  ...item.document,
                                  maxSizeMB: Number(event.target.value),
                                },
                              })
                            }
                          />
                        </VStack>
                        <VStack className="gap-2">
                          <Label htmlFor={`${idPrefix}-max-files-${item.id}`}>
                            Maximum files
                          </Label>
                          <Input
                            id={`${idPrefix}-max-files-${item.id}`}
                            type="number"
                            min={1}
                            max={20}
                            value={item.document?.maxFiles ?? 1}
                            onChange={(event) =>
                              update(item.id, {
                                document: {
                                  ...item.document,
                                  maxFiles: Number(event.target.value),
                                },
                              })
                            }
                          />
                        </VStack>
                        <VStack className="gap-2 sm:col-span-2">
                          <Label>Accepted file types</Label>
                          <HStack className="flex-wrap items-center gap-4">
                            {FILE_TYPES.map(([label, mime]) => (
                              <CheckBoxWithLabel
                                key={mime}
                                id={`${idPrefix}-file-type-${
                                  item.id
                                }-${label.toLowerCase()}`}
                                label={label}
                                labelclassname="ml-2 text-sm text-primaryText"
                                className="h-4 w-4 accent-primary"
                                checked={(
                                  item.document?.acceptedTypes ?? []
                                ).includes(mime)}
                                onChange={(event) => {
                                  const types =
                                    item.document?.acceptedTypes ?? [];
                                  update(item.id, {
                                    document: {
                                      ...item.document,
                                      acceptedTypes: event.target.checked
                                        ? [...types, mime]
                                        : types.filter((type) => type !== mime),
                                    },
                                  });
                                }}
                              />
                            ))}
                          </HStack>
                        </VStack>
                      </>
                    )}
                  </div>
                </SortableRequirementRow>
              ))}
            </VStack>
          </SortableContext>
        </DndContext>
        <HStack className="mt-6 flex-wrap items-end gap-3">
          <VStack className="min-w-40 gap-2">
            <Label htmlFor={`${idPrefix}-new-requirement-kind`}>
              Item type
            </Label>
            <Select
              id={`${idPrefix}-new-requirement-kind`}
              options={REQUIREMENT_KINDS}
              value={newKind}
              textGetter={kindLabel}
              onChange={(event) =>
                setNewKind(event.target.value as WorkflowRequirementKind)
              }
            />
          </VStack>
          <SecondaryCTAButton onClick={add}>Add item</SecondaryCTAButton>
        </HStack>
      </div>
    </section>
  );
}
